import React, { useEffect, useState } from "react";
import { Colors } from "../constants/Colors";
import logo from "../assets/images/ArkilaLogo.png";
import "../styles/editProfileModal.css";

/**
 * Edit form for the signed-in admin's profile.
 * Usage: <EditProfileModal isOpen={...} user={...} onClose={...} onSave={(updated) => Promise} />
 */
const EditProfileModal = ({ isOpen, user, onClose, onSave }) => {
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  // Reset the form to the current user whenever the modal opens.
  useEffect(() => {
    if (isOpen) {
      setName(user?.name || "");
      setEmail(user?.email || "");
      setErrors({});
      setSaveError("");
    }
  }, [isOpen, user]);

  // Close on Escape, lock body scroll while open.
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => e.key === "Escape" && !saving && onClose();
    document.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [isOpen, saving, onClose]);

  if (!isOpen) return null;

  const validate = () => {
    const next = {};
    if (!name.trim()) next.name = "Name is required.";
    if (!email.trim()) {
      next.email = "Email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      next.email = "Enter a valid email address.";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSaveError("");
    setSaving(true);
    try {
      await onSave?.({ ...user, name: name.trim(), email: email.trim() });
      onClose();
    } catch (err) {
      setSaveError(err?.message || "Couldn't save your changes. Try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="edit-profile-modal-backdrop"
      onClick={() => !saving && onClose()}
      role="presentation"
    >
      <div
        className="edit-profile-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-profile-modal-title"
        onClick={(e) => e.stopPropagation()}
        style={{
          "--modal-gradient-start": Colors.GRADIENT_START,
          "--modal-gradient-end": Colors.GRADIENT_END,
          "--modal-text-dark": Colors.TEXT_DARK,
          "--modal-text-light": Colors.TEXT_LIGHT,
          "--modal-border": Colors.BORDER_GRAY,
          "--modal-accent": Colors.PRIMARY,
          "--modal-danger": Colors.RED,
        }}
      >
        <button
          type="button"
          className="edit-profile-modal-close"
          onClick={onClose}
          disabled={saving}
          aria-label="Close"
        >
          <i className="bi bi-x-lg" />
        </button>

        {/* ── Banner ── */}
        <div className="edit-profile-modal-banner">
          <div className="edit-profile-modal-avatar-ring">
            <img src={logo} alt="" className="edit-profile-modal-avatar" />
          </div>
        </div>

        <form className="edit-profile-modal-body" onSubmit={handleSubmit} noValidate>
          <h3 id="edit-profile-modal-title" className="edit-profile-modal-title">
            Edit Profile
          </h3>
          <p className="edit-profile-modal-subtitle">
            Update the name and email tied to your admin account.
          </p>

          <div className="edit-profile-field">
            <label htmlFor="profile-name" className="edit-profile-label">
              Full name
            </label>
            <input
              id="profile-name"
              type="text"
              className={`edit-profile-input${errors.name ? " has-error" : ""}`}
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={saving}
              autoFocus
            />
            {errors.name && (
              <span className="edit-profile-error">{errors.name}</span>
            )}
          </div>

          <div className="edit-profile-field">
            <label htmlFor="profile-email" className="edit-profile-label">
              Email
            </label>
            <input
              id="profile-email"
              type="email"
              className={`edit-profile-input${errors.email ? " has-error" : ""}`}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={saving}
            />
            {errors.email && (
              <span className="edit-profile-error">{errors.email}</span>
            )}
          </div>

          <div className="edit-profile-field">
            <span className="edit-profile-label">Role</span>
            <div className="edit-profile-static">
              {user?.role || "Admin"}
              <span className="edit-profile-static-hint">Not editable</span>
            </div>
          </div>

          {saveError && (
            <p className="edit-profile-save-error">
              <i className="bi bi-exclamation-circle me-1" />
              {saveError}
            </p>
          )}

          <div className="edit-profile-actions">
            <button
              type="button"
              className="edit-profile-btn edit-profile-btn-secondary"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="edit-profile-btn edit-profile-btn-primary"
              disabled={saving}
            >
              {saving ? (
                <>
                  <span className="edit-profile-spinner" />
                  Saving…
                </>
              ) : (
                "Save Changes"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProfileModal;